import { State } from '@progress/kendo-data-query';
import { toODataString } from '@progress/kendo-data-query/dist/npm/odata.operators';
import { ItemAvailability } from 'src/types/itemAvailability';
import { ItemHistory } from 'src/types/itemHistory';
import { Item, ItemDetails } from 'src/types/itemList';
import { ItemSearchRequest } from 'src/types/itemSearchRequest';
import { ItemDocument } from 'src/types/itemDocument';
import { ItemUnits } from 'src/types/itemUnits';
import { ODataResponse } from 'src/types/oDataResponse';
import { OnOrderItem } from 'src/types/onOrderItem';
import { RestrictedItem } from 'src/types/restrictedItem';
import { get, post } from './Api';
import { HistoricalQuantity } from 'src/types/historicalQuantity';
import { WarehouseStockItem } from 'src/types/warehouseStockItemResult';

const getCurrentRawSearch = (dataState) => {
  var odataString = toODataString(dataState);
  if (dataState.sort.length === 0) {
    odataString += '&$orderby=ItemNumber_t_sort';
  }
  return odataString;
};

async function getItems(dataState: State, searchRequest: ItemSearchRequest): Promise<ODataResponse<Item>> {
  //Deep copy state, don't mutate the actual table state
  let odataState = { ...dataState };
  odataState.filter = { ...dataState.filter };
  odataState.filter.filters = [...dataState.filter.filters];

  if (searchRequest.vendorNumber) {
    odataState.filter.filters.push({
      field: 'VendorNumber',
      operator: 'eq',
      value: +searchRequest.vendorNumber,
    });
  }

  //Search Solr for items
  if (searchRequest.availableOnly) {
    let primaryWarehouse = searchRequest.warehouseCode;

    console.log('Search Available Only On Warehouse: ' + primaryWarehouse);

    odataState.filter.filters.push({
      field: 'Availability_' + primaryWarehouse + '_i',
      operator: 'ne',
      value: 0,
    });
  }

  if (!searchRequest.programId && !searchRequest.history) {
    odataState.filter.filters.push({
      field: 'Active_b',
      operator: 'eq',
      value: true,
    });
  }

  let odataString = getCurrentRawSearch(odataState);

  return post<ODataResponse<Item>>(`/odata/item/?$count=true&${odataString}`, searchRequest);
}

async function getItem(itemNumber: string): Promise<ItemDetails> {
  return get<ItemDetails>(`/item?itemNumber=${encodeURIComponent(itemNumber)}`);
}

async function getItemAvailability(itemNumber: string): Promise<ItemAvailability[]> {
  return get<ItemAvailability[]>(`/item/availability?itemNumber=${encodeURIComponent(itemNumber)}`);
}

async function getItemAvailabilityForShipdate(itemNumber: string, shipdate: Date, warehouseCode: string): Promise<any> {
  return get<any[]>(`/item/${itemNumber}/availability?shipdate=${shipdate}&warehouse=${warehouseCode}`);
}

async function getOnOrderItems(itemNumber: string, itemName: string, customerNumber: number): Promise<OnOrderItem[]> {
  return get<OnOrderItem[]>(`/item/onOrder/${customerNumber}?itemNumber=${encodeURIComponent(itemNumber)}&itemName=${encodeURIComponent(itemName)}`);
}

async function getItemHistory(itemNumber: string, itemName: string, customerNumber: number): Promise<ItemHistory[]> {
  return get<ItemHistory[]>(`/item/history/${customerNumber}?itemNumber=${encodeURIComponent(itemNumber)}&itemName=${encodeURIComponent(itemName)}`);
}

async function getItemUnits(itemNumber: string): Promise<ItemUnits[]> {
  return get<ItemUnits[]>(`/item/units?itemNumber=${encodeURIComponent(itemNumber)}`);
}

async function getItemDetails(itemNumber: string): Promise<ItemDetails> {
  return get<ItemDetails>(`/item/detail?itemNumber=${encodeURIComponent(itemNumber)}`);
}

async function getCustomerPrice(itemNumber: string, customerNumber: string, warehouseCode: string, quantity: number, defaultPrice: number, shipToNumber: string): Promise<number> {
  return get<number>(
    `/item/price/${customerNumber}?itemNumber=${encodeURIComponent(itemNumber)}&warehouseCode=${encodeURIComponent(warehouseCode)}&quantity=${encodeURIComponent(
      quantity
    )}&defaultPrice=${encodeURIComponent(defaultPrice)}&shipToNumber=${encodeURIComponent(shipToNumber)}`
  );
}

async function getNonContractedItemStatus(itemNumber: string, customerNumber: string): Promise<RestrictedItem> {
  return get<RestrictedItem>(`/item/non-contracted?itemNumber=${encodeURIComponent(itemNumber)}&customerNumber=${customerNumber}`);
}

async function addItemRestrictionCheck(itemNumber: string, customerNumber: string, shipToState: string, shipToZip: number, vendorNumber: string): Promise<RestrictedItem> {
  return get<RestrictedItem>(
    `/item/item-restriction-check?itemNumber=${encodeURIComponent(itemNumber)}&customerNumber=${customerNumber}&shipToState=${shipToState}&shipToZip=${shipToZip}&vendorNumber=${vendorNumber}`
  );
}

async function getHistoricalQuantity(searchRequest: ItemSearchRequest): Promise<any> {
  return post<any>('/item/historical-quantity', searchRequest);
}

async function getCustomItemHistory(searchRequest: ItemSearchRequest): Promise<HistoricalQuantity[]> {
  return post<HistoricalQuantity[]>('/item/custom', searchRequest);
}

async function getItemDocuments(itemNumber: string): Promise<ItemDocument[]> {
  return get<ItemDocument[]>(`/item/documents?itemNumber=${encodeURIComponent(itemNumber)}`);
}

async function getDNRItemsOnOrder(sfaOrderId: number, warehouseCode: string): Promise<WarehouseStockItem[]> {
  return get<WarehouseStockItem[]>(`/item/dnr?sfaOrderId=${sfaOrderId}&warehouse=${warehouseCode}`);
}

export default {
  getItems,
  getItemDetails,
  getItemAvailability,
  getItemAvailabilityForShipdate,
  getOnOrderItems,
  getItemHistory,
  getItemUnits,
  getItem,
  getCustomerPrice,
  getNonContractedItemStatus,
  getHistoricalQuantity,
  getItemDocuments,
  getCustomItemHistory,
  getDNRItemsOnOrder,
  addItemRestrictionCheck,
};
